import { Button, DetailBox, addToast } from '@octano/global-ui';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackToListButton from '../../../../components/buttons/GoBackToListButton';
import Loading from '../../../../components/info/Loading';
import { useActionKitImp } from '../../../../components/ozmoit/common';
import SectionTitle from '../../../../components/text/SectionTitle';
import { TranslationsKeys } from '../../../../locales/translations';

import {
  getCourseGroupDetailAction,
  setCourseGroupItemsAction,
} from '../actions';
import ChildrenSearchSelect from '../parts/ChildrenSearchSelect';
import CourseGroupDetailModal from '../parts/CourseGroupDetailModal';
import CreateCourseGroupModal from '../parts/CreateCourseGroupModal';
import ItemsAssignmentTable from '../parts/ItemsAssignmentTable';
import ItemsFilters, { ItemsFiltersValue } from '../parts/ItemsFilters';
import { ChildSearchItem, ChildType, CourseGroupDetail } from '../types';
import {
  extractBackendErrorCode,
  extractCycleConflicts,
} from '../utils/extractBackendErrorCode';

// Inlined to avoid circular import (config/routes.ts imports this file).
// Mirrors PathsLayouts.academicOffers + '/course-groups'.
const LIST_PATH = '/academic-offers/course-groups';

/**
 * HU 15571 — Vista de gestión de items de una Agrupación de actividades.
 *
 * Carga el detalle (cursos + sub-agrupaciones), permite agregar/quitar
 * elementos en estado local y persiste en bloque vía
 * setCourseGroupItemsAction (full-replace) al guardar.
 *
 * - Editar metadatos (nombre/código/escuela): icono ✏️ del summary card
 *   abre <CreateCourseGroupModal> en modo edición; al éxito refresca el
 *   `detail` del padre sin disparar el spinner global (initializedRef
 *   guarda que ya hidratamos al menos una vez).
 * - Branchea errores del save en COURSE_GROUP_CYCLE y
 *   COURSE_GROUP_ITEMS_NOT_FOUND con mensajes específicos; cualquier otro
 *   error cae al toast genérico.
 * - `?created=1` (redirect post-creación de FE-C) muestra un toast de
 *   éxito y limpia el query param vía history.replace.
 */
export default function CourseGroupItems(): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const history = useHistory();
  const location = useLocation();
  const initializedRef = useRef<boolean>(false);

  const [detail, setDetail] = useState<CourseGroupDetail | null>(null);
  const [courses, setCourses] = useState<Map<number, ChildSearchItem>>(
    new Map(),
  );
  const [groups, setGroups] = useState<Map<number, ChildSearchItem>>(new Map());
  const [filters, setFilters] = useState<ItemsFiltersValue>({ type: 'course' });
  const [editOpen, setEditOpen] = useState<boolean>(false);
  // Id de la sub-agrupación cuyo modal de detalle está abierto. null = cerrado.
  const [detailGroupId, setDetailGroupId] = useState<number | null>(null);

  const { action: fetchDetail, loading: loadingDetail } = useActionKitImp(
    getCourseGroupDetailAction,
    {
      onSuccess: (d) => {
        setDetail(d);
        // Hidratamos los Maps locales con lo que ya tiene la agrupación,
        // para que al guardar (full-replace) no perdamos lo persistido si
        // el usuario solo agrega/quita unos pocos items.
        const courseMap = new Map<number, ChildSearchItem>();
        d.courses.forEach((c) => {
          courseMap.set(c.id, {
            id: c.id,
            name: c.name,
            code: c.code,
            school: d.school,
            modality: c.modality,
            academicLevel: c.academicLevel,
            credits: c.credits,
            type: 'course',
          });
        });
        const groupMap = new Map<number, ChildSearchItem>();
        d.childGroups.forEach((g) => {
          groupMap.set(g.id, {
            id: g.id,
            name: g.name,
            code: g.code,
            school: g.school,
            modality: null,
            academicLevel: null,
            credits: null,
            type: 'group',
          });
        });
        setCourses(courseMap);
        setGroups(groupMap);
        initializedRef.current = true;
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('errors.notFound'),
          });
          history.push(LIST_PATH);
        },
      },
    },
  );

  useEffect(() => {
    if (groupId) {
      fetchDetail(groupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // ?created=1 viene del modal de creación (FE-C). Mostramos el toast y
  // limpiamos el query param para que un refresh no lo dispare otra vez.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('created') === '1') {
      addToast({
        icon: 'success',
        color: 'success',
        text: t('items.successCreated'),
      });
      history.replace(`${LIST_PATH}/${groupId}/items`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { action: saveItems, loading: saving } = useActionKitImp(
    setCourseGroupItemsAction,
    {
      onSuccess: () => {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('items.messages.saved'),
        });
        history.push(LIST_PATH);
      },
      onError: {
        unknown: (error) => {
          const code = extractBackendErrorCode(error);
          let text = t('errors.generic');
          if (code === 'COURSE_GROUP_CYCLE') {
            // Resolvemos los nombres de las sub-agrupaciones en conflicto
            // contra el Map local (las que el usuario acaba de intentar
            // agregar en esta sesión). Si por alguna razón el id no está
            // en el Map (edge case), caemos al mensaje genérico de ciclo.
            const conflictIds = extractCycleConflicts(error);
            const names = conflictIds
              .map((cid) => groups.get(cid)?.name)
              .filter(
                (n): n is string => typeof n === 'string' && n.length > 0,
              );
            text =
              names.length > 0
                ? t('errors.cycleDetectedWithNames', {
                    names: names.join(', '),
                  })
                : t('errors.cycleDetected');
          }
          if (code === 'COURSE_GROUP_ITEMS_NOT_FOUND') {
            text = t('errors.itemsNotFound');
          }
          addToast({ icon: 'error', color: 'danger', text });
        },
      },
    },
  );

  const handleSave = useCallback(() => {
    saveItems(groupId, {
      courseIds: Array.from(courses.keys()),
      childGroupIds: Array.from(groups.keys()),
    });
  }, [saveItems, groupId, courses, groups]);

  const handleAdd = useCallback((item: ChildSearchItem) => {
    if (item.type === 'course') {
      setCourses((prev) => {
        const next = new Map(prev);
        next.set(item.id, item);
        return next;
      });
    } else {
      setGroups((prev) => {
        const next = new Map(prev);
        next.set(item.id, item);
        return next;
      });
    }
  }, []);

  const handleShowDetail = useCallback((id: number) => {
    setDetailGroupId(id);
  }, []);

  const handleRemove = useCallback((idToRemove: number, type: ChildType) => {
    if (type === 'course') {
      setCourses((prev) => {
        const next = new Map(prev);
        next.delete(idToRemove);
        return next;
      });
    } else {
      setGroups((prev) => {
        const next = new Map(prev);
        next.delete(idToRemove);
        return next;
      });
    }
  }, []);

  // Ids a excluir del buscador en función del tipo activo. Para `group`
  // sumamos el groupId actual (defense-in-depth: el backend ya lo excluye).
  const excludeIds = useMemo<Set<number>>(() => {
    if (filters.type === 'course') {
      return new Set(courses.keys());
    }
    return new Set([...Array.from(groups.keys()), groupId]);
  }, [filters.type, courses, groups, groupId]);

  if (loadingDetail && !initializedRef.current) {
    return <Loading insideCard />;
  }

  if (!detail) {
    return <></>;
  }

  return (
    <Card className="p-4 mt-2 mx-3">
      {/* Row 1: GoBack on the left + DetailBox + edit pencil on the right */}
      <Row className="align-items-center pt-3">
        <Col xs="auto">
          <GoBackToListButton path={LIST_PATH} />
        </Col>
        <Col xs={12} md={8} className="ml-auto">
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <DetailBox
                columns={[
                  {
                    title: t('items.summary.name'),
                    body: detail.name,
                    size: { xs: 12, md: 5 },
                  },
                  {
                    title: t('items.summary.code'),
                    body: detail.code,
                    size: { xs: 12, md: 3 },
                  },
                  {
                    title: t('items.summary.responsibleSchool'),
                    body: detail.school?.name ?? '-',
                    size: { xs: 12, md: 4 },
                  },
                ]}
              />
            </div>
            <Button
              text=""
              icon="edit"
              size="sm"
              outlined
              onClick={() => setEditOpen(true)}
              aria-label={t('items.summary.edit')}
            />
          </div>
        </Col>
      </Row>

      {/* Row 2: section title + subtitle */}
      <Row className="mt-4">
        <Col xs={12}>
          <SectionTitle text={t('items.section')} />
          <p className="fs-16 text-light mt-3 mb-3">{t('items.subtitle')}</p>
        </Col>
      </Row>

      <ItemsFilters onApply={setFilters} />

      <Row className="mb-3">
        <Col xs={12}>
          <ChildrenSearchSelect
            groupId={groupId}
            filters={filters}
            excludeIds={excludeIds}
            onSelect={handleAdd}
          />
        </Col>
      </Row>

      <ItemsAssignmentTable
        courses={courses}
        groups={groups}
        onRemove={handleRemove}
        onShowDetail={handleShowDetail}
      />

      <Row className="justify-content-end pt-4" style={{ gap: 8 }}>
        <Button
          type="button"
          text={t('items.actions.cancel')}
          outlined
          onClick={() => history.push(LIST_PATH)}
          disabled={saving}
        />
        <Button
          type="button"
          text={t('items.actions.save')}
          onClick={handleSave}
          loading={saving}
        />
      </Row>

      <CreateCourseGroupModal
        isOpen={editOpen}
        courseGroup={detail}
        onClose={() => setEditOpen(false)}
        onSuccess={(updated) => {
          if (updated) {
            setDetail(updated);
          }
          setEditOpen(false);
        }}
      />

      {detailGroupId !== null && (
        <CourseGroupDetailModal
          isOpen={true}
          groupId={detailGroupId}
          onClose={() => setDetailGroupId(null)}
        />
      )}
    </Card>
  );
}
